// NumericKeyboard.js
import React from 'react';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const NumericKeyboard = ({ onInput, onBackspace, onEnter, eventType, isTrackEvent, disabled = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Estilos responsive para los botones - BOTONES MÁS GRANDES
  const getButtonStyle = () => ({
    width: isMobile ? "50px" : isMobileOrTablet ? "60px" : "70px",
    height: isMobile ? "50px" : isMobileOrTablet ? "60px" : "70px",
    fontSize: isMobile ? "1.2rem" : isMobileOrTablet ? "1.4rem" : "1.6rem",
    minWidth: "unset",
    fontWeight: "bold",
    borderRadius: isMobile ? "6px" : "8px",
    boxShadow: theme.shadows[1],
    transition: "all 0.2s ease",
    padding: "0",
    '&:hover': {
      transform: !disabled ? "translateY(-2px)" : "none",
      boxShadow: !disabled ? theme.shadows[4] : theme.shadows[1],
    },
    '&:active': {
      transform: !disabled ? "translateY(0)" : "none",
    }
  });

  // Layout responsive - ajustado para botones más grandes
  const getGridStyle = () => ({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: isMobile ? 0.5 : isMobileOrTablet ? 0.8 : 1,
    maxWidth: isMobile ? "220px" : isMobileOrTablet ? "280px" : "320px",
    margin: "0 auto",
  });

  // Layout diferente para pista vs campo
  const buttons = isTrackEvent ? [
    ["7", "8", "9", "⌫"],
    ["4", "5", "6", ":"],
    ["1", "2", "3", "."],
    ["", "0", "", "↵"],
  ] : [
    ["7", "8", "9", "⌫"],
    ["4", "5", "6", "-"],
    ["1", "2", "3", "+"],
    ["", "0", ".", "↵"],
  ];

  const getButtonColor = (key) => {
    if (disabled && key !== "") {
      return {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
      };
    }

    switch (key) {
      case "↵":
        return {
          backgroundColor: theme.palette.success.main,
          color: "#fff",
          '&:hover': {
            backgroundColor: theme.palette.success.dark,
            transform: !disabled ? "translateY(-2px)" : "none",
            boxShadow: !disabled ? `0 6px 16px ${theme.palette.success.main}40` : theme.shadows[1],
          }
        };
      case "⌫":
        return {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.main,
            transform: !disabled ? "translateY(-2px)" : "none",
            boxShadow: !disabled ? `0 6px 16px ${theme.palette.error.main}40` : theme.shadows[1],
          }
        };
      case "-":
      case "+":
      case ":":
      case ".":
        return {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            transform: !disabled ? "translateY(-2px)" : "none",
            boxShadow: !disabled ? `0 6px 16px ${theme.palette.primary.main}40` : theme.shadows[1],
          }
        };
      default:
        return {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.grey[300]}`,
          '&:hover': {
            backgroundColor: theme.palette.grey[200],
            transform: !disabled ? "translateY(-2px)" : "none",
            boxShadow: !disabled ? theme.shadows[4] : theme.shadows[1],
          }
        };
    }
  };

  return (
    <Box sx={getGridStyle()}>
      {buttons.flat().map((key, index) => (
        <Button
          key={index}
          sx={{
            ...getButtonStyle(),
            ...getButtonColor(key),
          }}
          onClick={() => {
            if (disabled) return;
            
            if (key === "⌫") onBackspace();
            else if (key === "↵") onEnter();
            else if (key === "-" || key === "+" || key === ":" || key === ".") {
              // Para botones especiales, enviar como input normal
              onInput(key);
            }
            else if (key !== "") onInput(key);
          }}
          disabled={key === "" || disabled}
          variant="contained"
        >
          {key}
        </Button>
      ))}
    </Box>
  );
};

export default NumericKeyboard;